import {
  JobCardPdfAction,
  postV1CompaniesCompanyIdJobCardPdfs,
  PostV1CompaniesCompanyIdJobCardPdfsResponse,
  postV1CompaniesCompanyIdOrdersDispatchJobCardPdfs,
  PostV1CompaniesCompanyIdOrdersDispatchJobCardPdfsResponse,
  postV1CompaniesCompanyIdOrdersOrderIdJobsJobCardPdfs,
  PostV1CompaniesCompanyIdOrdersOrderIdJobsJobCardPdfsResponse,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback } from 'react';

import useRenderedPdfDownload from '~hooks/useRenderedPdfDownload/useRenderedPdfDownload';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import buildDispatchSearchRequest from '../useOrders/buildDispatchSearchRequest';
import buildOrderJobsFilter from '../useOrders/buildOrderJobsFilter';
import { JobCardPdfRecipientType, toPdfServiceRecipientType } from './models';

type RenderJobCardPdfs = () => Promise<{
  data:
    | PostV1CompaniesCompanyIdJobCardPdfsResponse
    | PostV1CompaniesCompanyIdOrdersDispatchJobCardPdfsResponse
    | PostV1CompaniesCompanyIdOrdersOrderIdJobsJobCardPdfsResponse;
}>;

export default function useBulkJobCardPdfs() {
  const { ordersAssignedStore, ordersDispatchStore, userStore } = useStores();
  const { downloadRenderedPdf } = useRenderedPdfDownload();

  const downloadJobCardPdfs = useCallback(
    (render: RenderJobCardPdfs) =>
      // No timeout: a multi-job render has no size bound, like the other bulk downloads.
      downloadRenderedPdf({
        failureMessage: t('dispatch.job_card.generation_failed'),
        render,
      }),
    [downloadRenderedPdf],
  );

  const generateJobCardPdfsForOrder = useCallback(
    (orderId: string, recipientType: JobCardPdfRecipientType) =>
      downloadJobCardPdfs(() =>
        postV1CompaniesCompanyIdOrdersOrderIdJobsJobCardPdfs({
          path: {
            'company-id': getEffectiveUserCompanyId(userStore),
            'order-id': orderId,
          },
          body: {
            ...buildOrderJobsFilter({
              assignedFilters: ordersAssignedStore.filters,
              dispatchFilters: ordersDispatchStore.filters,
              pathname: location.pathname,
            }),
            recipient_type: toPdfServiceRecipientType(recipientType),
            render_action: JobCardPdfAction.DOWNLOAD,
          },
        }),
      ),
    [downloadJobCardPdfs, ordersAssignedStore, ordersDispatchStore, userStore],
  );

  const generateJobCardPdfsForJobs = useCallback(
    (jobIds: string[], recipientType: JobCardPdfRecipientType) =>
      downloadJobCardPdfs(() =>
        postV1CompaniesCompanyIdJobCardPdfs({
          path: { 'company-id': getEffectiveUserCompanyId(userStore) },
          body: {
            job_ids: jobIds,
            recipient_type: toPdfServiceRecipientType(recipientType),
            render_action: JobCardPdfAction.DOWNLOAD,
          },
        }),
      ),
    [downloadJobCardPdfs, userStore],
  );

  const generateJobCardPdfsForDispatch = useCallback(
    (recipientType: JobCardPdfRecipientType) =>
      downloadJobCardPdfs(() =>
        postV1CompaniesCompanyIdOrdersDispatchJobCardPdfs({
          path: { 'company-id': getEffectiveUserCompanyId(userStore) },
          body: {
            ...buildDispatchSearchRequest(ordersDispatchStore.filters, {
              includeDates: true,
            }),
            recipient_type: toPdfServiceRecipientType(recipientType),
            render_action: JobCardPdfAction.DOWNLOAD,
          },
        }),
      ),
    [downloadJobCardPdfs, ordersDispatchStore, userStore],
  );

  return {
    generateJobCardPdfsForDispatch,
    generateJobCardPdfsForJobs,
    generateJobCardPdfsForOrder,
  };
}
